import { GraduationCap, Award, MapPin } from "lucide-react";
import { whatsappUrl } from "./constants";

const PORTRAIT =
  "https://static.wixstatic.com/media/d16770_02a888758a964673a20f160601b69874~mv2.jpg/v1/fill/w_1905,h_993,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/d16770_02a888758a964673a20f160601b69874~mv2.jpg";

const CREDENTIALS = [
  { year: "2021", label: "Laurea Triennale", detail: "Scienza della Nutrizione · Università di Urbino" },
  { year: "2023", label: "Laurea Magistrale", detail: "Scienze della Nutrizione Umana · Università di Parma" },
  { year: "2024", label: "Master SANIS", detail: "Nutrizione Sportiva di alto livello" },
  { year: "Italia", label: "Diploma Personal Trainer", detail: "Approfondimento nutrizione & training" },
];

const CERTS = [
  "ISAK (antropometria)",
  "Ricomposizione Corporea",
  "Coaching in Nutrizione",
  "Diete Cicliche",
  "Protocolli Chetogeni",
  "Intuitive Eating",
  "NutriProof Academy",
  "I.A.E. & A.P.N.",
];

export const ChiSono = () => {
  return (
    <section
      id="chi-sono"
      data-testid="chi-sono-section"
      className="relative bg-[#F7F9F7] py-20 md:py-28 border-y border-[#E1E7E3]"
    >
      <div className="max-w-[1280px] mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Portrait + studio info */}
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden soft-shadow border border-[#E1E7E3] aspect-[4/5]">
              <img src={PORTRAIT} alt="Dr. Christian Sacchetto" className="w-full h-full object-cover" />
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <Stat value="350+" label="Percorsi seguiti" />
              <Stat value="20+" label="Certificazioni" />
            </div>
          </div>

          {/* Bio */}
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.22em] text-[#1F6E45] font-bold mb-4">
              · Chi sono
            </div>
            <h2 className="font-display text-[#0E1A14] text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Dr. Christian Sacchetto.
              <br />
              <span className="text-[#1F6E45]">Nutrizionista sportivo.</span>
            </h2>

            <div className="mt-6 space-y-4 text-[#4A5A52] text-base md:text-lg leading-relaxed max-w-2xl">
              <p>
                Sono un nutrizionista appassionato di sport, soprattutto nel
                contesto della palestra. Ho conseguito il diploma da Personal
                Trainer in Italia e ho approfondito lo studio della nutrizione
                sportiva con il Master SANIS.
              </p>
              <p>
                Mi dedico a persone tra i 16 e i 50 anni che vogliono migliorare
                la composizione corporea attraverso l'allenamento. Collaboro con
                sportivi di diverse discipline:{" "}
                <span className="text-[#0E1A14] font-semibold">
                  powerlifting, bodybuilding, maratona, ciclismo, calcio e
                  nuoto.
                </span>
              </p>
              <p>
                Credo fermamente nell'aggiornamento continuo: ogni piano è
                costruito sulla letteratura più recente, non su mode passeggere.
              </p>
            </div>

            {/* Timeline */}
            <div className="mt-10 grid sm:grid-cols-2 gap-3">
              {CREDENTIALS.map((c) => (
                <div
                  key={c.label}
                  className="bg-white border border-[#E1E7E3] rounded-xl p-4 flex items-start gap-3"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#E8F2EC] grid place-items-center text-[#1F6E45] flex-shrink-0">
                    <GraduationCap size={18} strokeWidth={1.8} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#1F6E45] font-bold">
                      {c.year}
                    </div>
                    <div className="font-heading font-bold text-[#0E1A14] text-sm">{c.label}</div>
                    <div className="text-xs text-[#6F7E76] mt-0.5">{c.detail}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Certs chip cloud */}
            <div className="mt-8">
              <div className="flex items-center gap-2 mb-4 text-xs uppercase tracking-[0.22em] text-[#1F6E45] font-bold">
                <Award size={14} /> Aggiornamenti & specializzazioni
              </div>
              <div className="flex flex-wrap gap-2">
                {CERTS.map((c) => (
                  <span
                    key={c}
                    className="px-3 py-1.5 rounded-full bg-white border border-[#E1E7E3] text-[#4A5A52] text-xs font-medium"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <a
                href={whatsappUrl()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="chisono-cta"
                className="inline-flex items-center gap-2 bg-[#1F6E45] text-white px-6 py-3.5 rounded-full font-semibold text-sm hover:bg-[#144A2E] transition-colors"
              >
                Prenota la prima consulenza
              </a>
              <span className="inline-flex items-center gap-2 text-[#4A5A52] text-sm">
                <MapPin size={16} className="text-[#1F6E45]" />
                In presenza & online
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ value, label }) => (
  <div className="bg-white border border-[#E1E7E3] rounded-xl p-4">
    <div className="font-display text-2xl text-[#1F6E45]">{value}</div>
    <div className="text-[11px] uppercase tracking-[0.18em] text-[#6F7E76] font-semibold mt-1">
      {label}
    </div>
  </div>
);
